<!-- Part of the SPARKL educational activity system, Copyright 2019 by Pepper Williams -->
<template>
	<k-dialog v-model="dialog_open" persistent scrollable aria-labelledby="satchel-admin-users-title" @keydown.esc="$emit('dialog_cancel')">
		<k-card>
			<k-card-title style="border-bottom:1px solid #999">
				<b id="satchel-admin-users-title">User Admin Tool</b>
				<k-spacer></k-spacer>
				<k-btn icon accessible-label="Open user admin help" @click="show_help" color="light-blue"><k-icon large>fas fa-info-circle</k-icon></k-btn>
				<k-btn small color="secondary" @click="$emit('dialog_cancel')" class="ml-4 mr-1"><k-icon small class="mr-2">fas fa-times</k-icon>Done</k-btn>
			</k-card-title>
			<k-card-text class="mt-3" style="font-size:16px; color:#000;">
				<div v-if="all_users" style="clear:both">
					<div class="py-4 d-flex">
						<k-text-field
							v-model="search"
							prepend-inner-icon="fa fa-search" prepend-decorative clearable clear-icon="fa fa-times-circle"
							accessible-label="Search users"
							label="Search" single-line hide-details outlined dense background-color="#fff" style="flex:0 1 450px"
						></k-text-field>
						<k-btn class="ml-8" color="primary" @click="new_user_clicked"><k-icon small class="mr-2">fas fa-plus</k-icon> Create New User</k-btn>
						<k-btn class="ml-8" color="primary" @click="export_users_csv"><k-icon small class="mr-2">fas fa-file-export</k-icon> Export Users</k-btn>
					</div>

					<v-data-table light dense
						:headers="headers"
						:items="rows"
						:custom-filter="table_search_filter"
						:search="search"
						:footer-props="footer_options"
						:items-per-page="100"
						class="k-admin-users-table"
					>
						<template v-slot:item="{ item }"><tr>
							<td><k-btn icon x-small class="mr-2" accessible-label="Edit user" @click="edit_user(item.user_id)"><k-icon>fas fa-edit</k-icon></k-btn>{{item.last_name}}, {{item.first_name}} ({{item.email}})</td>
							<td class="text-center"><nobr>{{system_role_display(item.system_role)}}</nobr></td>
							<td class="text-center"><nobr>{{date_string(item.created_at)}}</nobr></td>
							<td class="text-center"><nobr>{{date_string(item.last_login)}}</nobr></td>
							<td class="text-center">{{item.login_count}}</td>
							<td class="text-center">{{item.secure_framework_access_count}}</td>

							<td v-if="instance_requires_registration||instance_has_organizations" class="text-center">{{item.organization}}</td>
							<td v-if="instance_uses_category_access" class="text-center cat-access-col">{{item.category_access}}</td>
							<td v-if="instance_requires_registration" class="text-center"><b v-if="item.reg_request" @click="edit_user(item.user_id)" class="py-1 px-2 pink darken-3 white--text" style="cursor:pointer">{{item.reg_request}}</b></td>
							
							<td v-if="instance_has_subdomains" class="text-center">{{item.allow_to_create_frameworks_for_category}}</td>
							<td v-if="enable_framework_notifications" class="text-center">{{opt_in_display(item.notification_settings)}}</td>
						</tr></template>
					</v-data-table>
				</div>

				<div class="mt-3" style="font-size:14px; line-height:18px;">
					<div>* FD Ct. = Framework Download count: the number of frameworks the user has downloaded (since 7 August 2025)</div>
					<div v-if="instance_has_subdomains" class="mt-2">* Fr. Create = If non-empty, the user has been granted a special ability to create new frameworks for the listed subdomain (note that users with system editor and system admin rights can create new frameworks for any subdomain)</div>
					<div v-if="enable_framework_notifications" class="mt-2">* Opt In = Whether or not the user has opted in to receive weekly notifications of updated frameworks</div>
				</div>

				<AdminUserEdit v-if="user_being_edited" 
					:user="user_being_edited" 
					:instance_requires_registration="instance_requires_registration"
					:instance_uses_category_access="instance_uses_category_access"
					:instance_has_organizations="instance_has_organizations"
					:instance_has_subdomains="instance_has_subdomains"
					@editor_cancel="user_being_edited=null" 
					@user_edited="user_edited" 
					@user_deleted="user_deleted" 
					@send_token_login="send_token_login"
				></AdminUserEdit>
			</k-card-text>
		</k-card>
	</k-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import AdminUserEdit from './AdminUserEdit'

export default {
	components: { AdminUserEdit },
	props: {
	},
	data() { return {
		dialog_open: true,
		all_users: null,
		footer_options: {
			itemsPerPageOptions: [10,50,100,-1],
		},
		search: '',
		user_being_edited: null,
	}},
	computed: {
		...mapState(['user_info']),
		...mapGetters(['home_category', 'instance_code']),
		enable_framework_notifications() {
			return this.$store.state.site_config.enable_framework_notifications == 'true'
		},
		instance_requires_registration() {
			if (this.instance_code == 'rosetta_exchange') return true
			return false
		},
		instance_uses_category_access() {
			if (this.instance_code == 'rosetta_exchange') return true
			return false
		},
		instance_has_organizations() {
			if (this.instance_code == 'rosetta_exchange' || this.instance_code == 'satchel_commons') return true		//  || this.instance_code == 'rosetta_align'
			return false
		},
		instance_has_subdomains() {
			if (this.instance_code == 'satchel_commons' || this.instance_code == 'rosetta_align') return true
			return false
		},
		headers() {
			let arr = [
				{ text: 'User', align: 'left', sortable: true, value:'name_search' },
				{ text: 'Rights', align: 'center', sortable: true, value:'system_role_sort' },
				{ text: 'Date Created', align: 'center', sortable: true, value:'created_at' },
				{ text: 'Last Login', align: 'center', sortable: true, value:'last_login' },
				{ text: 'Login Ct.', align: 'center', sortable: true, value:'login_count' },
				{ text: 'FD Ct.*', align: 'center', sortable: true, value:'secure_framework_access_count' },
			]
			// add some things for special instances
			if (this.instance_requires_registration || this.instance_has_organizations) {
				arr.push({ text: 'Organization', align: 'center', sortable: true, value:'organization' })
			}
			if (this.instance_uses_category_access) {
				arr.push({ text: 'Cat. Access', align: 'center', sortable: true, value:'category_access', width: '120px', class: 'cat-access-col' })
			}
			if (this.instance_requires_registration) {
				arr.push({ text: 'Reg. Request', align: 'center', sortable: true, value:'reg_request' })
			}
			if (this.instance_has_subdomains) {
				arr.push({ text: 'Fr. Create*', align: 'center', sortable: true, value:'allow_to_create_frameworks_for_category' })
			}
			if (this.enable_framework_notifications) {
				arr.push({ text: 'Opt In*', align: 'center', sortable: true, value:'opt_in_sort' })
			}
			return arr
		},
		rows() {
			console.warn('compute rows')
			let arr = []
			for (let user of this.all_users) {
				// start with all the user fields
				let o = $.extend(true, {}, user)

				// and a searchable value for name/email
				o.name_search = sr('$1 $2 $3', o.last_name, o.first_name, o.email).toLowerCase()

				// sort by opt in status
				if (o.notification_settings?.on == 'yes') o.opt_in_sort = 1
				else o.opt_in_sort = 0

				// if instance_has_subdomains, add a column for showing whether each user has the special "allow to create frameworks" flag on for a subdomain
				if (this.instance_has_subdomains) {
					o.allow_to_create_frameworks_for_category == o.allow_to_create_frameworks_for_category ?? ''
				}

				// for instances that require registration, if category_access_requested is non-empty, there is a request that needs to be approved
				if (this.instance_requires_registration) {
					o.reg_request = o.category_access_requested
				}

				// add a sort val
				if (o.system_role == 'admin') o.system_role_sort = 1
				else if (o.system_role == 'editor') o.system_role_sort = 2
				else if (o.system_role == 'reviewer') o.system_role_sort = 3
				else o.system_role_sort = 4
				arr.push(o)
			}

			// initial sort; relying on sort-by in the data-table made it so that if you edited a user you'd lose what you were sorting on
			arr.sort((a,b)=>{
				if (a.system_role_sort != b.system_role_sort) {
					return (a.system_role_sort - b.system_role_sort)
				}
				if (a.created_at != b.created_at) {
					if (a.created_at < b.created_at) return 1
					if (a.created_at > b.created_at) return -1
				}
				return 0
			})
			return arr
		}
	},
	created() {
		// DEBUG
		vapp.all_users_component = this
		this.load_admin_user_list()
	},
	mounted() {
	},
	methods: {
		show_help() { vapp.show_help('roles_system') },

		load_admin_user_list() {
			let payload = {}
			U.loading_start()
			U.ajax('admin_get_all_users', payload, result=>{
				U.loading_stop()
				if (result.status != 'ok') {
					console.log('Error in admin ajax call'); vapp.ping(); return
				}

				// remove unsignedin@cglt.com user
				let index = result.all_users.findIndex(x=>x.email == 'unsignedin@cglt.com')
				if (index > -1) result.all_users.splice(index, 1)

				this.all_users = result.all_users
				// format for each admin_user: { user_id, system_role, email, first_name, last_name, created_at, last_login, login_count }
			})
		},

		system_role_display(system_role) {
			if (system_role == 'admin') return 'System Admin'
			if (system_role == 'editor') return 'System Editor'
			if (system_role == 'reviewer') return 'System Reviewer'
			else return 'Default'
		},

		date_string(gmt_date) {
			if (empty(gmt_date)) return '–'
			if (gmt_date == 'TODAY') return 'Just Now'
			return date.format(U.convert_to_local_date(gmt_date), 'M/D/YYYY h:mm A')	// Jan 1, 2019 3:12 PM
		},

		// initially, framework update report is the only opt-in option
		opt_in_display(notification_settings) {
			if (notification_settings && notification_settings.on == 'yes') return 'yes'
			return 'no'
		},

		table_search_filter(value, search, item) {
			// value is the value of the column (we can ignore this); search is the search string (could be empty)
			// RETURN FALSE TO HIDE THE ITEM

			// if search is empty, always return true, so the row will SHOW
			if (empty(search)) return true

			search = search.toLowerCase()
			let re = new RegExp(search, 'i')

			// check email/name
			if (item.name_search.search(re) > -1) return true

			// check rights
			if (this.system_role_display(item.system_role).search(re) > -1) return true

			// if we get to here return false
			return false
		},

		edit_user(user_id) {
			this.user_being_edited = this.all_users.find(x=>x.user_id==user_id)
		},

		user_edited(payload) {
			payload.user_id = this.user_info.user_id
			console.log(payload)

			U.loading_start()
			U.ajax('admin_save_user', payload, result=>{
				U.loading_stop()
				if (result.status != 'ok') {
					console.log('Error in admin ajax call'); vapp.ping(); return
				}

				// if we just created a new user, send "invitation email"
				if (payload.user_id_being_edited == 0) {
					this.send_token_login({user_email: this.user_being_edited.email, body_html: sr('An account has been created for this email address ($1), allowing you to sign in to $2 as a registered user.<br/><br/>Click the following link to sign in for the first time. This link can only be used once, and will expire in 24 hours. After clicking the link to sign in, you can set or change your account password.', this.user_being_edited.email, this.$store.state.site_config.app_name)})
				}

				// result.saved_user_id will contain the new user_id if this is a new user
				this.user_being_edited.user_id = result.saved_user_id
				this.user_being_edited.first_name = payload.first_name
				this.user_being_edited.last_name = payload.last_name
				this.user_being_edited.system_role = (payload.system_role == '*CLEAR*') ? '' : payload.system_role
				this.user_being_edited.allow_to_create_frameworks_for_category = (payload.allow_to_create_frameworks_for_category == '*CLEAR*') ? '' : payload.allow_to_create_frameworks_for_category
				this.user_being_edited.organization = (payload.organization == '*CLEAR*') ? '' : payload.organization
				this.user_being_edited.org_job_title = (payload.org_job_title == '*CLEAR*') ? '' : payload.org_job_title
				this.user_being_edited.organization_type = (payload.organization_type == '*CLEAR*') ? '' : payload.organization_type
				this.user_being_edited.category_access = (payload.category_access == '*CLEAR*') ? '' : payload.category_access
				this.user_being_edited.category_access_requested = (payload.category_access_requested == '*CLEAR*') ? '' : payload.category_access_requested

				// close user editor
				this.user_being_edited = null
			})
		},

		user_deleted() {
			// if user_id was 0, it was a new user, so just delete
			if (this.user_being_edited.user_id == 0) {
				this.all_users.splice(this.all_users.length-1, 1)
				this.user_being_edited = null
				return
			}

			this.$confirm({
				title: 'Are you sure?',
				text: sr('Are you sure you want to delete user <b>$1</b>?', this.user_being_edited.email),
				acceptText: 'Delete User',
				acceptColor: 'red',
				dialogMaxWidth: 600
			}).then(y => {
				let payload = {
					user_id: this.user_info.user_id,
					user_id_to_delete: this.user_being_edited.user_id,
				}
				U.loading_start()
				U.ajax('admin_delete_user', payload, result=>{
					U.loading_stop()
					if (result.status != 'ok') {
						console.log('Error in admin ajax call: ' + result.status); vapp.ping(); return
					}

					let index = this.all_users.findIndex(x=>x==this.user_being_edited)
					this.all_users.splice(index, 1)

					// close user editor
					this.user_being_edited = null
				})
			}).catch(n=>{console.log(n)}).finally(f=>{})
		},

		new_user_clicked() {
			this.$prompt({
				title: 'Add New User',
				text: 'Enter the new user’s email address:',
				initialValue: '',
				acceptText: 'Add User',
			}).then(email => {
				email = $.trim(email).toLowerCase()

				if (empty(email) || email.indexOf('@') == -1) {
					this.$alert('Please enter a valid user email.').then(x=>{this.new_user_clicked()})
					return
				}
				for (let identifier in this.all_users) {
					if (this.all_users[identifier].email == email) {
						this.search = email
						this.$alert('This user is already in the system. Click the edit button next to the user’s name to edit their account data.')
						return
					}
				}
				this.new_user_create(email)
			}).catch(n=>{console.log(n)}).finally(f=>{})
		},

		new_user_create(email) {
			let u = new User_Info()
			u.user_id = 0
			u.system_role = ''
			u.email = email
			u.first_name = ''
			u.last_name = ''
			u.created_at = 'TODAY'
			u.last_login = ''
			u.login_count = 0
			this.all_users.push(u)
			this.user_being_edited = this.all_users[this.all_users.length-1]
		},

		send_token_login(payload) {
			// send token_signin_email, with token lasting 24 hours and sending in received body_html (which may be empty)
			payload.token_duration = 60*24
			U.loading_start()
			U.ajax('send_token_signin_email', payload, result=>{
				U.loading_stop()
				let msg
				if (result.status != 'ok') {
					console.log('Error in token signin ajax call: ' + result.status)
					msg = 'The one-time sign-in link could not be sent.'
				} else if (payload.body_html) {
					// if we sent a body_html, it means that this was a new user, so show a corresponding message
					msg = 'The new user was created, and an email has been sent to the user with a one-time sign-in link. After clicking that link to sign in, they can change their password. Please let the new user know that <b>the link will be valid for only 24 hours</b>, and that they should <b>check their spam or junk folder</b> if they don’t receive the link.'
				} else {
					msg = 'One-time sign-in link sent. Please let the new user know that <b>the link will be valid for only 24 hours</b>, and that they should <b>check their spam or junk folder</b> if they don’t receive the link.'
				}
				this.$alert(msg)
			})

		},

		export_users_csv() {
			let table = [[
				'User Last Name',
				'User First Name',
				'User Email',
				'Rights',
				'Date Created',
				'Last Login',
				'Login Ct.',
				'FD Ct.*',
			]]

			if (this.instance_requires_registration || this.instance_has_organizations) {
				table[0].push('Organization')
			}

			if (this.instance_uses_category_access) {
				table[0].push('Cat. Access')
			}

			if (this.instance_requires_registration) {
				table[0].push('Reg. Request')
			}

			if (this.instance_has_subdomains) {
				table[0].push('Fr. Create*')
			}

			if (this.enable_framework_notifications) {
				table[0].push('Opt In*')
			}

			for (let item of this.rows) {
				let row = [
					`${item.last_name}`, 
					`${item.first_name}`,
					`${item.email}`,
					this.system_role_display(item.system_role),
					this.date_string(item.created_at),
					this.date_string(item.last_login),
					item.login_count,
					item.secure_framework_access_count,
				]

				if (this.instance_requires_registration || this.instance_has_organizations) {
					row.push(item.organization ?? '')
				}

				if (this.instance_uses_category_access) {
					row.push(item.category_access)
				}

				if (this.instance_requires_registration) {
					row.push(item.reg_request ?? '')
				}

				if (this.instance_has_subdomains) {
					row.push(item.allow_to_create_frameworks_for_category ?? '')
				}

				if (this.enable_framework_notifications) {
					row.push(this.opt_in_display(item.notification_settings))
				}

				table.push(row)
			}

			let csv = CSV.stringify(table)

			const today = new Date().toISOString().slice(0, 10)

			U.download_file(csv, `Users-${today}.csv`)
		},
	}
}
</script>

<style lang="scss">
.k-admin-users-table {
	td {font-size:12px!important;}

	.cat-access-col {
		width: 120px;
		max-width: 120px;
		white-space: normal !important;
		overflow-wrap: anywhere;
		word-break: break-word;
	}
}
</style>
